import { BusinessObjectBase } from './base';
import { tutorSchema } from '@schemas/business-objects/tutor';
import type { TermTutor } from './term-tutor';
import type { User } from './user';
import type { Email, Id } from '../base-types';
import type { Role } from './role';
import type { RoleAssignment } from './role-assignment';

export interface TutorData {
  readonly id: Id;
  readonly title?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: Email;
  readonly inactive?: boolean;
  readonly appUserId: Id;
  readonly appUser?: User;
  readonly termTutors?: TermTutor[];
}

export interface Tutor extends Readonly<TutorData> {}
export class Tutor extends BusinessObjectBase {
  static readonly entityName = 'Tutor' as const;
  static readonly plural = 'tutors';
  static schema = tutorSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Tutor[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Tutor(data));
  }

  static async findOne(filter?: any): Promise<Tutor | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Tutor(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Tutor> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Tutor(data);
  }

  static async create(data: Omit<TutorData, 'id'>): Promise<Tutor> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Tutor(result);
  }

  static async createMany(data: Omit<TutorData, 'id'>[]): Promise<Tutor[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Tutor(item));
  }

  static async updateById(id: Id, updates: Partial<TutorData>): Promise<Tutor> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Tutor(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async createAppUser(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Tutor.plural}/${(this as any).id}/create-app-user`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
